// app/javascript/three/workflow_runtime_manager.js

export function initWorkflowRuntime({ sceneData }) {
  const steps = Array.isArray(sceneData?.workflow_steps)
    ? [...sceneData.workflow_steps].sort((a, b) => Number(a.position || 0) - Number(b.position || 0))
    : [];

  const state = {
    steps,
    currentIndex: 0,
    completedSteps: [],
    skippedSteps: [],
    lastBlocked: null,
    history: [],
    ended: false,
  };

  function finish() {
    if (state.ended) return;
    state.ended = true;
    pushHistory({ type: "ended" });
  }

  function currentStep() {
    return state.steps[state.currentIndex] || null;
  }

  function nextStep() {
    return state.steps[state.currentIndex + 1] || null;
  }

  function hasWorkflow() {
    return state.steps.length > 0;
  }

  function isCompleted() {
    return state.currentIndex >= state.steps.length;
  }

  function normalizeSeverity(step) {
    return step?.severity || "required";
  }

  function normalizeMessage(step) {
    const msg = step?.message;
    return typeof msg === "string" && msg.trim() !== "" ? msg.trim() : null;
  }

  function pushHistory(entry) {
    state.history.push({
      at: Date.now(),
      ...entry,
    });
  }

  function allow(extra = {}) {
    return { ok: true, ...extra };
  }

  function describeAttempt(kind, payload = {}) {
    return {
      attempted_kind: kind,
      tool_id: payload.toolId ?? null,
      hotspot_id: payload.hotspotId ?? null,
    };
  }

  function matchesStep(step, attemptedKind, payload = {}) {
    if (!step) return false;
    if (step.kind !== attemptedKind) return false;

    if (attemptedKind === "tool_click") {
      return String(step.pulse_tool_id) === String(payload.toolId);
    }

    if (attemptedKind === "tool_drop") {
      return (
        String(step.pulse_tool_id) === String(payload.toolId) &&
        String(step.hotspot_id) === String(payload.hotspotId)
      );
    }

    if (attemptedKind === "hotspot_flow") {
      return String(step.hotspot_id) === String(payload.hotspotId);
    }

    return false;
  }

  function isAlreadyCompletedAttempt(attemptedKind, payload = {}) {
    return state.completedSteps.some((step) => matchesStep(step, attemptedKind, payload));
  }

  function completeStepAtIndex(index, meta = {}) {
    const step = state.steps[index];
    if (!step) return allow(meta);

    state.completedSteps.push(step);
    pushHistory({ type: "completed", step, ...meta });

    if (index === state.currentIndex) {
      state.currentIndex += 1;
    } else if (index > state.currentIndex) {
      state.currentIndex = index + 1;
    }

    return {
      ok: true,
      advanced: true,
      completed: isCompleted(),
      completed_step: step,
      current_step: currentStep(),
      ...meta,
    };
  }

  function skipStepAtIndex(index, meta = {}) {
    const step = state.steps[index];
    if (!step) return allow(meta);

    state.skippedSteps.push(step);
    pushHistory({ type: "skipped", step, ...meta });

    if (index === state.currentIndex) {
      state.currentIndex += 1;
    } else if (index > state.currentIndex) {
      state.currentIndex = index + 1;
    }

    return {
      ok: true,
      advanced: true,
      skipped: true,
      skipped_step: step,
      completed: isCompleted(),
      current_step: currentStep(),
      ...meta,
    };
  }

  function skipRange(fromIndex, toIndexExclusive, meta = {}) {
    const skipped = [];

    for (let i = fromIndex; i < toIndexExclusive; i += 1) {
      const step = state.steps[i];
      if (!step) continue;

      skipped.push(step);
      state.skippedSteps.push(step);
      pushHistory({ type: "skipped", step, ...meta });
    }

    state.currentIndex = Math.max(state.currentIndex, toIndexExclusive);

    return skipped;
  }

  function block(step, reason, meta = {}) {
    state.lastBlocked = {
      reason,
      step,
      severity: normalizeSeverity(step),
      message: normalizeMessage(step),
      ...meta,
    };

    pushHistory({ type: "blocked", reason, step, ...meta });

    console.log("[WORKFLOW] blocked:", state.lastBlocked);

    return {
      ok: false,
      blocked: true,
      reason,
      step,
      severity: normalizeSeverity(step),
      message: normalizeMessage(step),
      completed: isCompleted(),
      current_step: step,
      ...meta,
    };
  }

  function ignore(attemptedKind, payload = {}, extra = {}) {
    const attempt = describeAttempt(attemptedKind, payload);

    pushHistory({
      type: "ignored",
      ...attempt,
      ...extra,
    });

    return {
      ok: true,
      ignored: true,
      advanced: false,
      completed: isCompleted(),
      current_step: currentStep(),
      ...attempt,
      ...extra,
    };
  }

  function completeCurrentStep(attemptedKind, payload = {}) {
    const step = currentStep();
    const attempt = describeAttempt(attemptedKind, payload);
    const severity = normalizeSeverity(step);
    const message = normalizeMessage(step);

    return completeStepAtIndex(state.currentIndex, {
      step,
      severity,
      message: severity === "optional" ? message : null,
      feedback_tone: severity === "optional" ? "positive" : "neutral",
      ...attempt,
    });
  }

  function findReachableMatchThroughSkippableChain(attemptedKind, payload = {}) {
    const start = state.currentIndex;
    if (start >= state.steps.length) return null;

    for (let i = start + 1; i < state.steps.length; i += 1) {
      const prev = state.steps[i - 1];
      const prevSeverity = normalizeSeverity(prev);

      // Required steps cannot be skipped; optional and warning steps can.
      if (!["optional", "warning"].includes(prevSeverity)) {
        return null;
      }

      const step = state.steps[i];
      if (!step) return null;

      if (matchesStep(step, attemptedKind, payload)) {
        return {
          matchedIndex: i,
          matchedStep: step,
        };
      }
    }

    return null;
  }

  function handleRequiredMismatch(attemptedKind, payload = {}) {
    const step = currentStep();
    const attempt = describeAttempt(attemptedKind, payload);

    return block(step, "required_step_not_completed", {
      expected_kind: step?.kind ?? null,
      expected_tool_id: step?.pulse_tool_id ?? null,
      expected_hotspot_id: step?.hotspot_id ?? null,
      feedback_tone: "danger",
      ...attempt,
    });
  }

  function handleSkippableMismatch(attemptedKind, payload = {}) {
    const step = currentStep();
    const attempt = describeAttempt(attemptedKind, payload);

    const reachable = findReachableMatchThroughSkippableChain(attemptedKind, payload);

    if (reachable) {
      const { matchedIndex, matchedStep } = reachable;

      const skippedSteps = skipRange(state.currentIndex, matchedIndex, {
        skip_reason: "skippable_chain_skipped_by_future_match",
        landed_step: matchedStep,
        ...attempt,
      });

      const matchedSeverity = normalizeSeverity(matchedStep);
      const matchedMessage = normalizeMessage(matchedStep);
      const warningMessages = skippedSteps
        .filter((skipped) => normalizeSeverity(skipped) === "warning")
        .map((skipped) => ({
          message: normalizeMessage(skipped) || "Attenzione: hai saltato un passaggio consigliato",
          tone: "warning",
        }));
      if (warningMessages.length && matchedSeverity === "optional" && matchedMessage) {
        warningMessages.push({ message: matchedMessage, tone: "positive" });
      }

      const completedResult = completeStepAtIndex(matchedIndex, {
        step: matchedStep,
        severity: matchedSeverity,
        message: warningMessages.length ? null : matchedMessage,
        feedback_messages: warningMessages,
        feedback_tone:
          matchedSeverity === "optional"
            ? "positive"
            : "warning",
        reached_via_optional_skip: true,
        skipped_steps: skippedSteps,
        ...attempt,
      });

      return {
        ...completedResult,
        skipped_optional_chain: true,
        skipped_steps: skippedSteps,
      };
    }

    return ignore(attemptedKind, payload, {
      feedback_tone: "warning",
      out_of_sequence: true,
    });
  }

  function resolveAttempt(attemptedKind, payload = {}) {
    const step = currentStep();

    if (!step) {
      return allow({
        completed: true,
        advanced: false,
        current_step: null,
        ...describeAttempt(attemptedKind, payload),
      });
    }

    if (matchesStep(step, attemptedKind, payload)) {
      return completeCurrentStep(attemptedKind, payload);
    }

    const severity = normalizeSeverity(step);

    if (severity === "required") {
      return handleRequiredMismatch(attemptedKind, payload);
    }

    return handleSkippableMismatch(attemptedKind, payload);
  }

  function tryToolClick(toolId) {
    if (state.ended) return { ok: false, ended: true, advanced: false };
    const payload = { toolId };

    if (isAlreadyCompletedAttempt("tool_click", payload)) {
      return ignore("tool_click", payload, { already_completed: true });
    }

    return resolveAttempt("tool_click", payload);
  }

  function tryToolDrop(toolId, hotspotId) {
    if (state.ended) return { ok: false, ended: true, advanced: false };
    const payload = { toolId, hotspotId };

    if (isAlreadyCompletedAttempt("tool_drop", payload)) {
      return ignore("tool_drop", payload, { already_completed: true });
    }

    return resolveAttempt("tool_drop", payload);
  }

  function tryHotspotFlow(hotspotId) {
    if (state.ended) return { ok: false, ended: true, advanced: false };
    const payload = { hotspotId };

    if (isAlreadyCompletedAttempt("hotspot_flow", payload)) {
      return ignore("hotspot_flow", payload, { already_completed: true });
    }

    return resolveAttempt("hotspot_flow", payload);
  }

  function debug() {
    console.log("[WORKFLOW] steps:", state.steps);
    console.log("[WORKFLOW] current step:", currentStep());
  }

  debug();

  return {
    state,
    currentStep,
    nextStep,
    hasWorkflow,
    isCompleted,
    finish,
    tryToolClick,
    tryToolDrop,
    tryHotspotFlow,
  };
}